/**
 * /it/comparisons/notion-ai — Draftlize vs Notion (Italian). Links to
 * /it/registro-delle-decisioni.
 */
import { MarketingShell } from '../MarketingShell';

export function NotionAiIt() {
  return (
    <MarketingShell
      currentLocale="it"
      hreflangPath="/comparisons/notion-ai"
      hreflangLocales={['it']}
      documentTitle="Alternativa a Notion per il prodotto: Draftlize vs Notion · Draftlize"
      documentDescription="Notion archivia pagine; Draftlize è un grafo di schede che un agente IA legge e scrive, così le decisioni non derivano. Confronto affiancato."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Confronto · vs Notion</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            Draftlize vs Notion:<br />
            <em>quale per il tuo lavoro di prodotto?</em>
          </h1>
          <p className="lede">
            Risposta breve: Notion è lo strumento migliore per scrivere le cose. Draftlize è un substrato dove gli agenti IA possono <em>leggere e scrivere</em> il tuo pensiero di prodotto come schede strutturate. Risolvono problemi adiacenti: più che sostituire l'uno con l'altro, si tratta di capire quale ti serve davvero adesso.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">In sintesi</span>
            <h2 className="serif">Fianco a fianco, <em>7 dimensioni.</em></h2>
          </div>
          <p>Dalla stessa roadmap e dagli stessi scenari eseguiti in entrambi gli strumenti.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Notion</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Forma del substrato</td><td className="ours yes">Grafo di schede tipizzate</td><td className="partial">Pagine di blocchi</td></tr>
            <tr><td>L'IA legge le decisioni come contesto ogni volta</td><td className="ours yes">nativo</td><td className="no">—</td></tr>
            <tr><td>L'IA riscrive come schede strutturate</td><td className="ours yes">nativo</td><td className="partial">scrive blocchi, senza struttura</td></tr>
            <tr><td>Propagazione «stale» al cambio di una dipendenza</td><td className="ours yes">nativo</td><td className="no">—</td></tr>
            <tr><td>Documenti ricchi e database</td><td className="no">—</td><td className="yes">eccellente</td></tr>
            <tr><td>Wiki di team · permessi · collaborazione</td><td className="no">solo singolo (v1)</td><td className="yes">eccellente</td></tr>
            <tr><td>Integrazione Claude Code / MCP</td><td className="ours yes">nativo</td><td className="no">—</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Dove <em>vince Notion.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Se ti serve una wiki</h3>
            <p>Notion è la migliore wiki esistente. Se il problema è «dove tiene il team il runbook», Notion. Draftlize non prova a essere una wiki.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Se vivi tra tabelle e database</h3>
            <p>Il sistema di database e viste di Notion è imbattibile. Tracker di roadmap, griglie di sprint, tabelle di valutazione: per queste forme, le collezioni strutturate di Notion vincono sempre.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Se il team è grande</h3>
            <p>Collaborazione in tempo reale, permessi granulari, commenti, menzioni: Notion è nato per questo. Draftlize è single-first per scelta.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Dove <em>vince Draftlize.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">IA che <em>davvero</em> ricorda</h3>
            <p>La «memoria» di Notion AI è un recupero best-effort nella chat. In Draftlize il substrato in cui lavora l'agente sono le schede, quindi legge la tua decisione sui prezzi a ogni chat, in automatico. Rispiegare va a zero.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Il drift diventa visibile</h3>
            <p>Cambi una pagina Notion → nessuno se ne accorge. Cambi una scheda di decisione in Draftlize → ogni scheda dipendente si segna <code>stale</code>. Il substrato traccia l'invalidazione come un sistema di build.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Contesto di prima classe per Claude Code / Cursor / MCP</h3>
            <p>Notion AI vive in Notion. Le schede di Draftlize atterrano nel repo via CLI ed emergono via MCP in ogni strumento IA. Le decisioni sono richiamabili da qualsiasi tool. Prosegui con <a href="/it/registro-delle-decisioni" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>registro delle decisioni</a>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Notion risponde a «dove scrive le cose il team». Draftlize risponde a «dove si accumula il pensiero di prodotto quando l'IA fa metà del lavoro».
        </blockquote>
        <cite>Se ti serve solo la prima risposta, Notion basta.</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Inizia gratis con <em>$5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Registrati e ricevi $5</span>
          <span>Paghi solo ciò che usi</span>
          <span>Il saldo non scade mai</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Puoi tenere Notion. Draftlize è il substrato sotto le parti assistite dall'IA. Registrati e ricevi $5, paghi solo ciò che usi, il saldo non scade mai.
          </p>
          <a className="btn-primary" href="/">Inizia gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
